import type { CompProps, GenericFieldConfig } from '@neo/blocks/forms/types'
import { BadgeInput } from '@neo/blocks/input-with-badge'
import { Flag } from '@neo/primitives/icon'
import { OptionalLabel } from '@neo/primitives/label'
import { REGIONS } from 'v2/configs/constants'
import { DataRegionNote } from 'v2/shared/components/data-region-note'
import { z } from 'zod'

export const createOrganizationSchema = z.object({
  organization_name: z
    .string()
    .min(1, 'Organization name is required')
    .max(100, 'Organization name must be less than 100 characters'),
  region: z.string().min(1, 'Data region is required'),
  invite_members: z.array(z.string().email()).optional(),
})

export type CreateOrganizationFormValues = z.infer<typeof createOrganizationSchema>

const regionOptions = [
  {
    value: REGIONS.US,
    label: 'United States',
    icon: <Flag iso="US" size={16} circle />,
  },
  {
    value: REGIONS.IN,
    label: 'India',
    icon: <Flag iso="IN" size={16} circle />,
  },
]

const InviteMembersBadgeInput = ({
  value,
  onChange,
  disabled,
}: CompProps<CreateOrganizationFormValues, unknown>) => {
  const emails = Array.isArray(value) ? value : []

  const handleValidate = (email: string, currentEmails: string[]) => {
    const emailToAdd = email.trim().toLowerCase()

    if (!z.string().email().safeParse(emailToAdd).success) {
      return 'Please enter a valid email address'
    }
    if (currentEmails.some((e) => e.toLowerCase() === emailToAdd)) {
      return 'This email has already been added'
    }
    return null
  }

  return (
    <div className="leading-none w-full min-h-[36px]">
      <OptionalLabel className="block mb-1.5">Invite members to join</OptionalLabel>
      <BadgeInput
        value={emails}
        className="px-3"
        onChange={onChange}
        disabled={disabled}
        placeholder="name@example.com"
        name="invite-members-badge-input"
        onValidate={handleValidate}
        acceptKeys={[',', 'Enter']}
      />
    </div>
  )
}

export const getCreateOrganizationFields = (
  existingOrgNames: string[] = [],
  opts: { lockedRegion?: string } = {},
): GenericFieldConfig<CreateOrganizationFormValues> => {
  const normalizedExistingNames = existingOrgNames.map((name) => name.toLowerCase().trim())
  // Free trial: lock the data region to the single region the user may create in.
  const { lockedRegion } = opts
  const availableRegionOptions = lockedRegion
    ? regionOptions.filter((option) => option.value === lockedRegion)
    : regionOptions

  return [
    {
      name: 'organization_name',
      label: 'Organization Name',
      componentType: 'input',
      zodValidation: createOrganizationSchema.shape.organization_name.refine(
        (name) => !normalizedExistingNames.includes(name.toLowerCase().trim()),
        { message: 'Organization name already exists' },
      ),
      inputProps: {
        placeholder: 'Enter an organization name',
        'data-testid': 'input-organization-name',
      },
      className: 'col-span-full',
    },
    {
      name: 'region',
      label: 'Data Region',
      componentType: 'select',
      zodValidation: createOrganizationSchema.shape.region,
      disabled: lockedRegion ? () => true : undefined,
      inputProps: {
        placeholder: 'Select a region',
        options: availableRegionOptions,
        'data-testid': 'select-region',
      },
      eventDataExtractor: (e) => e,
      className: 'col-span-full',
    },
    {
      // Spells out what the selected region means for renting numbers, at the
      // moment of choosing it rather than on the buy-number blocker (SER-6631).
      name: 'region_note',
      type: 'basic',
      component: ({ form }) => {
        const region = form.useStore((state) => state.values.region)
        return <DataRegionNote region={region} className="col-span-full -mt-4" />
      },
    },
    {
      name: 'invite_members',
      type: 'custom',
      component: InviteMembersBadgeInput,
      zodValidation: createOrganizationSchema.shape.invite_members,
      eventDataExtractor: (e) => e,
      className: 'col-span-full',
    },
  ]
}

export const CREATE_ORGANIZATION_DEFAULT_VALUES: CreateOrganizationFormValues = {
  organization_name: '',
  region: REGIONS.US,
  invite_members: [],
}
